import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { phase1Api } from "../api/client";

const iconPaths = {
  overview: ["M4 13.5 12 6l8 7.5", "M6.5 12.5V20h11v-7.5", "M10 20v-5h4v5"],
  store: ["M5 10.5h14l-1-4H6l-1 4Z", "M6.5 10.5V19h11v-8.5", "M9.5 19v-5h5v5"],
  products: ["M6.5 8.5 12 5l5.5 3.5v7L12 19l-5.5-3.5v-7Z", "M6.8 8.7 12 12l5.2-3.3", "M12 12v6.8"],
  sellers: ["M7 8.2a4 4 0 0 1 8 0", "M4.8 19c.7-4 3-6.1 6.2-6.1s5.5 2.1 6.2 6.1", "M17 8h3v8h-3"],
  buyers: ["M8.2 10.2a3.3 3.3 0 1 0 0-6.6 3.3 3.3 0 0 0 0 6.6Z", "M3.8 19.5c.5-3.2 2-5 4.4-5s3.9 1.8 4.4 5", "M16.2 10.5a2.7 2.7 0 1 0 0-5.4", "M14.8 14.7c2.1.3 3.5 1.9 3.9 4.8"],
  transporters: ["M4 8h10v8H4z", "M14 11h3.4l2.6 3v2h-6", "M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z", "M17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"],
  documents: ["M7 4h7l3 3v13H7V4Z", "M14 4v4h4", "M9.5 12h5", "M9.5 15h5"],
  subscriptions: ["M7 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z", "M8 9h8", "M8 13h5"],
  orders: ["M6 4h12v16H6z", "M9 8h6", "M9 12h6", "M9 16h4"],
  enquiries: ["M5 6h14v9H9l-4 4V6Z", "M8.5 9.5h7", "M8.5 12.5h4"],
  finance: ["M5 19h14", "M7 16V9", "12 16V5", "17 16v-4", "M4 7l8-4 8 4"],
  wallet: ["M5 7.5h12.5a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5V7.5Z", "M5 7.5 15.5 5v2.5", "M15.5 13h4"],
  security: ["M12 4 18 6.5v5.2c0 3.8-2.4 6.6-6 8.3-3.6-1.7-6-4.5-6-8.3V6.5L12 4Z", "m9.5 12 1.7 1.7 3.4-3.7"],
  ai: ["M12 3.5v3", "M12 17.5v3", "M3.5 12h3", "M17.5 12h3", "M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z"],
  movement: ["M4 7h11", "M13 4l4 3-4 3", "M20 17H9", "M11 14l-4 3 4 3"],
  branches: ["M5 20V7l7-3 7 3v13", "M8 20v-4h3v4", "M8 10h2", "M14 10h2", "M14 14h2"],
  suppliers: ["M4 18h16", "M6 18V9l4-3 4 3v9", "M14 11h4v7", "M8 13h2", "M8 16h2"],
  settings: ["M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z", "M4 12h2", "M18 12h2", "M12 4v2", "M12 18v2"],
  notifications: ["M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z", "M10 21h4", "M9.5 17a2.5 2.5 0 0 0 5 0"],
  reviews: ["m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9L6.4 21l1-6.1L3 10.6l6.1-.9L12 3Z"],
};

const roleSections = {
  ADMIN: [
    { title: "Operations", items: [
      { label: "Overview", icon: "overview", to: "/portal" },
      { label: "Notifications", icon: "notifications", to: "/portal/notifications" },
      { label: "Review Moderation", icon: "reviews", to: "/portal/reviews" },
      { label: "Seller Verification", icon: "sellers", to: "/portal/sellers" },
      { label: "Marketplace Catalogue", icon: "products", to: "/portal/products" },
      { label: "Categories", icon: "products", to: "/portal/categories" },
      { label: "Business Accounts", icon: "store", to: "/portal/businesses" },
      { label: "Buyers", icon: "buyers", to: "/portal/buyers" },
    ]},
    { title: "Trust & Logistics", items: [
      { label: "Documents", icon: "documents", to: "/portal/documents" },
      { label: "Transporters", icon: "transporters", to: "/portal/transporters" },
      { label: "Transport Dashboard", icon: "movement", to: "/portal/transport" },
      { label: "Security Review", icon: "security", to: "/portal" },
    ]},
    { title: "Finance & Trade", items: [
      { label: "Enquiries", icon: "enquiries", to: "/portal/enquiries" },
      { label: "Orders", icon: "orders", to: "/portal/orders" },
      { label: "Deliveries", icon: "transporters", to: "/portal/deliveries" },
      { label: "Finance Centre", icon: "finance", to: "/portal/finance" },
      { label: "Subscriptions", icon: "subscriptions", to: "/portal/subscriptions" },
    ]},
    { title: "Intelligence", items: [
      { label: "AI Support", icon: "ai", to: "/portal/ai" },
    ]},
  ],
  MERCHANT: [
    { title: "Store Setup", items: [
      { label: "Merchant Home", icon: "overview", to: "/portal" },
      { label: "Notifications", icon: "notifications", to: "/portal/notifications" },
      { label: "My Store", icon: "store", to: "/portal/store" },
      { label: "Verification", icon: "documents", to: "/portal/store" },
    ]},
    { title: "Catalogue", items: [
      { label: "Products", icon: "products", to: "/portal/products" },
    ]},
    { title: "Sales & Finance", items: [
      { label: "Enquiries", icon: "enquiries", to: "/portal/enquiries" },
      { label: "Orders", icon: "orders", to: "/portal/orders" },
      { label: "Customer Reviews", icon: "reviews", to: "/portal/reviews" },
      { label: "Finance", icon: "finance", to: "/portal/finance" },
      { label: "Subscription", icon: "subscriptions", to: "/portal/subscriptions" },
      { label: "Documents", icon: "documents", to: "/portal/documents" },
    ]},
    { title: "Channels", items: [
      { label: "AI Support", icon: "ai", to: "/portal/ai" },
    ]},
  ],

  BUSINESS: [
    { title: "Business Setup", items: [
      { label: "Business Home", icon: "overview", to: "/portal" },
      { label: "Notifications", icon: "notifications", to: "/portal/notifications" },
      { label: "Business Profile", icon: "store", to: "/portal/business" },
      { label: "Storefront", icon: "sellers", to: "/portal/store" },
    ]},
    { title: "Operations", items: [
      { label: "Product Catalogue", icon: "products", to: "/portal/products" },
      { label: "Branches", icon: "branches", to: "/portal/business" },
      { label: "Suppliers", icon: "suppliers", to: "/portal/business" },
      { label: "Inventory", icon: "orders", to: "/portal/products" },
      { label: "Goods Movement", icon: "movement", to: "/portal/business" },
      { label: "Enquiries", icon: "enquiries", to: "/portal/enquiries" },
      { label: "Orders", icon: "orders", to: "/portal/orders" },
      { label: "Customer Reviews", icon: "reviews", to: "/portal/reviews" },
    ]},
    { title: "Finance & Control", items: [
      { label: "Finance", icon: "finance", to: "/portal/finance" },
      { label: "Subscription", icon: "subscriptions", to: "/portal/subscriptions" },
      { label: "Documents", icon: "documents", to: "/portal/documents" },
    ]},
    { title: "Channels", items: [
      { label: "AI Support", icon: "ai", to: "/portal/ai" },
    ]},
  ],
  COURIER: [
    { title: "Transporter", items: [
      { label: "Transporter Home", icon: "overview", to: "/portal" },
      { label: "Notifications", icon: "notifications", to: "/portal/notifications" },
      { label: "Profile & Services", icon: "transporters", to: "/portal/transport" },
      { label: "Delivery Requests", icon: "orders", to: "/portal/transport" },
      { label: "Delivery Reviews", icon: "reviews", to: "/portal/reviews" },
      { label: "Finance", icon: "finance", to: "/portal/finance" },
      { label: "Subscription", icon: "subscriptions", to: "/portal/subscriptions" },
      { label: "Documents", icon: "documents", to: "/portal/documents" },
      { label: "AI Support", icon: "ai", to: "/portal/ai" },
    ]},
  ],
  BUYER: [
    { title: "Buyer", items: [
      { label: "Buyer Home", icon: "overview", to: "/portal" },
      { label: "Notifications", icon: "notifications", to: "/portal/notifications" },
      { label: "Marketplace", icon: "products", to: "/" },
      { label: "Enquiries", icon: "enquiries", to: "/portal/enquiries" },
      { label: "Orders", icon: "orders", to: "/portal/orders" },
      { label: "My Reviews", icon: "reviews", to: "/portal/reviews" },
      { label: "Payments", icon: "finance", to: "/portal/finance" },
      { label: "AI Support", icon: "ai", to: "/portal/ai" },
    ]},
  ],
};

function getWorkspace(user) {
  if (!user) return "BUYER";
  if (user.is_platform_staff || user.is_superuser || ["SUPER_ADMIN", "ADMIN", "SUPPORT", "FINANCE", "OPERATIONS"].includes(user.account_type)) return "ADMIN";
  if (user.account_type === "SELLER") return "MERCHANT";
  if (user.account_type === "BUSINESS") return "BUSINESS";
  if (user.account_type === "COURIER") return "COURIER";
  return "BUYER";
}

function NavIcon({ name }) {
  return (
    <span className="portal-real-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
        {(iconPaths[name] || iconPaths.overview).map((path, index) => <path d={path} key={index} />)}
      </svg>
    </span>
  );
}

export default function PortalSidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const location = useLocation();
  const workspace = getWorkspace(user);
  const [entitlement, setEntitlement] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);
  useEffect(() => {
    if (!["MERCHANT", "BUSINESS", "COURIER"].includes(workspace)) {
      setEntitlement(null);
      return;
    }
    let active = true;
    phase1Api.subscriptionEntitlement().then((response) => {
      if (active) setEntitlement(response.data || null);
    }).catch(() => {
      if (active) setEntitlement(null);
    });
    return () => { active = false; };
  }, [workspace, user?.id]);
  const sections = useMemo(() => {
    const source = roleSections[workspace] || roleSections.BUYER;
    const status = String(entitlement?.status || "").toUpperCase();
    if (!entitlement || ["TRIAL", "ACTIVE", "PLATFORM_STAFF", "BUYER_ACCESS"].includes(status)) return source;
    const alwaysAvailable = new Set([
      "Merchant Home", "Business Home", "Transporter Home", "Notifications", "My Store", "Verification",
      "Business Profile", "Storefront", "Profile & Services", "Finance", "Subscription", "Documents",
    ]);
    const graceAvailable = new Set(["Products", "Product Catalogue", "Inventory", "Branches", "Suppliers", "Goods Movement", "Enquiries", "Orders", "Customer Reviews", "Delivery Requests", "Delivery Reviews"]);
    return source.map((section) => ({
      ...section,
      items: section.items.filter((item) => alwaysAvailable.has(item.label) || (status === "GRACE" && graceAvailable.has(item.label))),
    })).filter((section) => section.items.length);
  }, [workspace, entitlement]);
  const subtitle = workspace === "ADMIN" ? "Operations Suite" : workspace === "MERCHANT" ? "Merchant Workspace" : workspace === "BUSINESS" ? "Business Workspace" : workspace === "COURIER" ? "Transporter Workspace" : "Buyer Workspace";
  const subscriptionStatus = String(entitlement?.status || "").toUpperCase();
  const statusText = subscriptionStatus === "GRACE"
    ? "Renew your package to restore all tools."
    : subscriptionStatus && !["TRIAL", "ACTIVE", "PLATFORM_STAFF", "BUYER_ACCESS"].includes(subscriptionStatus)
    ? "Choose a package to unlock workspace tools."
    : "Workspace tools for the current account type.";

  return (
    <>
      <button
        className="portal-mobile-menu-btn"
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="Open workspace menu"
        aria-expanded={mobileOpen}
      >
        <span aria-hidden="true">☰</span>
        <strong>Menu</strong>
      </button>
      <button
        className={`portal-mobile-backdrop ${mobileOpen ? "is-visible" : ""}`}
        type="button"
        onClick={() => setMobileOpen(false)}
        aria-label="Close workspace menu"
        tabIndex={mobileOpen ? 0 : -1}
      />
      <aside className={`portal-sidebar-v2 refined-sidebar premium-dark-sidebar real-icon-sidebar workspace-sidebar ${mobileOpen ? "mobile-open" : ""}`}>
      <div className="portal-sidebar-topline">
        <div className="portal-brand-v2 refined-portal-brand premium-portal-brand">
          <img src="/tazy-logo.jpeg" alt="TAZY CART" />
          <div className="sidebar-text-hide">
            <strong>TAZY CART</strong>
            <span>{subtitle}</span>
          </div>
        </div>
        <button className="sidebar-collapse-btn premium-collapse-btn" type="button" onClick={onToggle} aria-label="Toggle sidebar">
          {collapsed ? "›" : "‹"}
        </button>
        <button className="portal-mobile-close-btn" type="button" onClick={() => setMobileOpen(false)} aria-label="Close workspace menu">×</button>
      </div>

      <div className="portal-status-card refined-status-card premium-status-card workspace-status-card">
        <div><span className="status-dot" /><strong className="sidebar-text-hide">{subtitle}</strong></div>
        <p className="sidebar-text-hide">{statusText}</p>
      </div>

      <nav className="portal-nav-sections refined-nav-sections premium-nav-sections real-nav-sections workspace-nav-sections">
        {sections.map((section) => (
          <section key={section.title}>
            <h4 className="sidebar-text-hide">{section.title}</h4>
            {section.items.map((item) => (
              <NavLink key={`${section.title}-${item.label}`} to={item.to} end={item.to === "/portal"} title={collapsed ? item.label : undefined} onClick={() => setMobileOpen(false)}>
                <NavIcon name={item.icon} />
                <em className="sidebar-text-hide">{item.label}</em>
              </NavLink>
            ))}
          </section>
        ))}
      </nav>
      </aside>
    </>
  );
}
